import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useActivateProsProfile,
  useDeactivateProsProfile,
  useMyProsAnalytics,
  useMyProsProfile,
  useProsSearch,
} from '@tark/shared/hooks';
import { Button, Input } from '@tark/shared/components';
import type { ProviderListingType, ProviderSearchResult } from '@tark/shared/types';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock3,
  DollarSign,
  Loader2,
  MapPin,
  Search,
  ShieldCheck,
  Star,
  Sparkles,
  UserRound,
  Wrench,
} from 'lucide-react';
import { AppPageFrame } from '../components/AppPageFrame';

const PROVIDER_TYPES: Array<{
  type: ProviderListingType;
  label: string;
  noun: string;
  title: string;
  icon: typeof UserRound;
}> = [
  {
    type: 'COACH',
    label: 'Coaches',
    noun: 'coach',
    title: 'Lessons, clinics, player development',
    icon: UserRound,
  },
  {
    type: 'STRINGER',
    label: 'Stringers',
    noun: 'stringer',
    title: 'Racquet setup and restringing',
    icon: Wrench,
  },
  {
    type: 'ACADEMY',
    label: 'Academies',
    noun: 'academy',
    title: 'Programs, courts, training centers',
    icon: Building2,
  },
];

const PUBLICATION_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Pending review',
  VERIFIED: 'Approved',
  PUBLISHED: 'Live',
  SUSPENDED: 'Suspended',
  ARCHIVED: 'Archived',
};

function formatTypes(types: ProviderListingType[]): string {
  return types
    .map((type) => {
      if (type === 'STRINGER') return 'Stringer';
      if (type === 'ACADEMY') return 'Academy';
      return 'Coach';
    })
    .join(' + ');
}

function formatLocation(result: ProviderSearchResult): string {
  if (result.city && result.state) return `${result.city}, ${result.state}`;
  return result.city || result.state || result.zipcode || 'Area on file';
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'P';
}

function getProfileImage(result: ProviderSearchResult): string | null {
  return result.profilePhotoFull640Url || result.profilePhotoThumb256Url || result.profilePhotoUrl || null;
}

function money(value?: number | null): string | null {
  if (value === null || value === undefined) return null;
  return `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function range(min?: number | null, max?: number | null): string | null {
  const minLabel = money(min);
  const maxLabel = money(max);
  if (minLabel && maxLabel && minLabel !== maxLabel) return `${minLabel}-${maxLabel}`;
  return minLabel || maxLabel;
}

function getRate(result: ProviderSearchResult): { value: string; label: string } | null {
  if (result.coachDetails) {
    const value = range(result.coachDetails.hourlyRateMin, result.coachDetails.hourlyRateMax);
    return value ? { value, label: 'per hour' } : null;
  }
  if (result.stringerDetails) {
    const value = range(result.stringerDetails.basePriceMin, result.stringerDetails.basePriceMax);
    return value ? { value, label: 'labor' } : null;
  }
  return null;
}

function getExperience(result: ProviderSearchResult): string | null {
  const years =
    result.coachDetails?.experienceYears ??
    result.stringerDetails?.experienceYears ??
    null;
  return years ? `${years} yrs` : null;
}

function getServiceMetric(result: ProviderSearchResult): string {
  if (typeof result.distanceMiles === 'number') return `${result.distanceMiles.toFixed(1)} mi`;
  if (result.serviceRadiusMiles) return `${result.serviceRadiusMiles} mi radius`;
  if (result.academyDetails?.numCourts) return `${result.academyDetails.numCourts} courts`;
  return 'Nearby';
}

function getTags(result: ProviderSearchResult): string[] {
  const tags = [
    ...(result.coachDetails?.lessonTypes || []),
    ...(result.coachDetails?.specialties || []),
    ...(result.stringerDetails?.stringBrands || []),
    ...(result.stringerDetails?.offersPickupDelivery ? ['Pickup'] : []),
    ...(result.academyDetails?.programsOffered || []),
    ...(result.sports || []),
  ];
  return Array.from(new Set(tags.filter(Boolean))).slice(0, 6);
}

function getAvailability(result: ProviderSearchResult): string {
  if (result.coachDetails?.availabilityNotes) return result.coachDetails.availabilityNotes;
  if (result.stringerDetails?.turnaroundHours) return `${result.stringerDetails.turnaroundHours} hr turnaround`;
  if (result.serviceRadiusMiles) return `Serves within ${result.serviceRadiusMiles} miles`;
  return formatLocation(result);
}

function shortBio(result: ProviderSearchResult): string {
  if (result.bio?.trim()) return result.bio.trim();
  if (result.academyDetails?.description?.trim()) return result.academyDetails.description.trim();
  return `${formatTypes(result.providerTypes)} in ${formatLocation(result)}.`;
}

function hasVerifiedBadge(result: ProviderSearchResult): boolean {
  return result.verificationLevel === 'IDENTITY' || result.verificationLevel === 'PRO';
}

export default function ProsPage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<ProviderListingType>('COACH');
  const [zipDraft, setZipDraft] = useState('');
  const [submittedZip, setSubmittedZip] = useState('');
  const [sportDraft, setSportDraft] = useState('tennis');
  const [submittedSport, setSubmittedSport] = useState('tennis');

  const currentTypeMeta = PROVIDER_TYPES.find((entry) => entry.type === selectedType) ?? PROVIDER_TYPES[0];
  const searchParams = useMemo(
    () => ({
      type: selectedType,
      zipcode: submittedZip || undefined,
      sport: submittedSport || undefined,
      limit: 12,
      page: 0,
    }),
    [selectedType, submittedSport, submittedZip]
  );

  const { data: searchResponse, isLoading, isError, refetch } = useProsSearch(searchParams, true);
  const { data: myProfile, isLoading: myProfileLoading } = useMyProsProfile(true);
  const { data: myAnalytics } = useMyProsAnalytics(Boolean(myProfile));
  const activateProfile = useActivateProsProfile();
  const deactivateProfile = useDeactivateProsProfile();

  const results = searchResponse?.items ?? [];
  const resultTotal = searchResponse?.totalItems ?? results.length;
  const locationLabel = submittedZip ? `near ${submittedZip}` : 'near you';
  const sportLabel = submittedSport || 'sports';
  const hasMyAnalytics = Boolean(
    myAnalytics && (myAnalytics.profileViews > 0 || myAnalytics.contactClicks > 0)
  );

  const runSearch = () => {
    setSubmittedZip(zipDraft.trim());
    setSubmittedSport(sportDraft.trim());
  };

  return (
    <AppPageFrame className="space-y-6">
      <section className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
        <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="p-5 md:p-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                TARK PROs
              </span>
              <span className="rounded-full border border-border bg-surface px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-text-muted">
                Published listings
              </span>
            </div>
            <h1 className="mt-4 max-w-4xl text-3xl font-semibold tracking-tight text-text md:text-4xl">
              Find {sportLabel.toLowerCase()} {currentTypeMeta.label.toLowerCase()} {locationLabel}
            </h1>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <HeroStat label="Results" value={String(resultTotal)} />
              <HeroStat label="Category" value={currentTypeMeta.label} />
              <HeroStat label="Sport" value={sportLabel} />
            </div>
          </div>

          <aside className="border-t border-border bg-surface p-5 xl:border-l xl:border-t-0">
            {myProfileLoading ? (
              <div className="flex items-center gap-2 text-sm font-medium text-text-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading profile
              </div>
            ) : myProfile ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">Your Public Profile</p>
                  <p className="mt-2 text-lg font-semibold text-text">{myProfile.displayName}</p>
                  <p className="mt-1 text-sm text-text-muted">
                    {PUBLICATION_STATUS_LABEL[myProfile.publicationStatus || 'DRAFT'] || myProfile.publicationStatus || 'Draft'}
                    {' / '}
                    {myProfile.publiclyVisible ? 'Visible' : 'Private'}
                  </p>
                </div>
                {hasMyAnalytics && myAnalytics ? (
                  <div className="grid grid-cols-2 gap-3">
                    <MiniStat label="Views" value={myAnalytics.profileViews} />
                    <MiniStat label="Contacts" value={myAnalytics.contactClicks} />
                  </div>
                ) : null}
                <div className="grid gap-2">
                  <Button size="sm" onClick={() => navigate('/pros/businesses')}>Manage businesses</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/pros/provider/${myProfile.id}`)}>
                    Preview Public Profile
                  </Button>
                  {myProfile.isActive ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deactivateProfile.mutate()}
                      isLoading={deactivateProfile.isPending}
                    >
                      Pause Profile
                    </Button>
                  ) : myProfile.publicationStatus === 'VERIFIED' || myProfile.publicationStatus === 'PUBLISHED' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => activateProfile.mutate()}
                      isLoading={activateProfile.isPending}
                    >
                      Go Live
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">For coaches and pros</p>
                  <p className="mt-2 text-lg font-semibold text-text">Create a Public Profile</p>
                </div>
                <Button size="sm" onClick={() => navigate('/pros/become')}>
                  Become a PRO
                </Button>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-white p-3 shadow-sm">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_180px_160px_auto] xl:items-end">
          <div className="grid gap-2 sm:grid-cols-3">
            {PROVIDER_TYPES.map((entry) => {
              const active = entry.type === selectedType;
              const Icon = entry.icon;
              return (
                <button
                  key={entry.type}
                  type="button"
                  onClick={() => setSelectedType(entry.type)}
                  className={`flex h-16 items-center gap-3 rounded-lg border px-3 text-left transition-colors ${
                    active
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border bg-white text-text-muted hover:border-primary/30 hover:text-text'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{entry.label}</span>
                    <span className="block truncate text-xs">{entry.title}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <Input
            label="Sport"
            value={sportDraft}
            onChange={(event) => setSportDraft(event.target.value)}
            placeholder="tennis"
          />
          <Input
            label="ZIP"
            value={zipDraft}
            onChange={(event) => setZipDraft(event.target.value)}
            placeholder="75024"
          />
          <div className="flex gap-2">
            <Button onClick={runSearch} leftIcon={<Search className="h-4 w-4" />}>Search</Button>
            {(submittedZip || submittedSport !== 'tennis') ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setZipDraft('');
                  setSubmittedZip('');
                  setSportDraft('tennis');
                  setSubmittedSport('tennis');
                }}
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="flex min-h-[32vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : isError ? (
        <section className="max-w-xl rounded-lg border border-border bg-white p-5 shadow-sm">
          <p className="text-lg font-semibold text-text">TARK PROs unavailable</p>
          <p className="mt-2 text-sm text-text-muted">Public profiles could not be loaded.</p>
          <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
        </section>
      ) : results.length ? (
        <section className="space-y-4">
          {results.map((result) => (
            <ProviderResultCard
              key={result.id}
              result={result}
              onView={() => navigate(`/pros/provider/${result.id}`)}
            />
          ))}
        </section>
      ) : (
        <section className="flex min-h-[280px] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-white p-8 text-center">
          <Search className="h-9 w-9 text-text-subtle" />
          <p className="mt-3 text-lg font-semibold text-text">No {currentTypeMeta.label.toLowerCase()} yet</p>
          <p className="mt-1 max-w-md text-sm text-text-muted">
            Try another sport, ZIP, or profile type.
          </p>
          <Button className="mt-5" onClick={() => navigate('/pros/become')}>Become a PRO</Button>
        </section>
      )}
    </AppPageFrame>
  );
}

function ProviderResultCard({
  result,
  onView,
}: {
  result: ProviderSearchResult;
  onView: () => void;
}) {
  const imageUrl = getProfileImage(result);
  const rate = getRate(result);
  const experience = getExperience(result);
  const tags = getTags(result);

  return (
    <article className="grid overflow-hidden rounded-lg border border-border bg-white shadow-sm transition-shadow hover:shadow-[var(--shadow-md)] lg:grid-cols-[300px_minmax(0,1fr)_230px]">
      <div className="relative min-h-[300px] bg-surface-elevated lg:min-h-full">
        {imageUrl ? (
          <img src={imageUrl} alt={result.displayName} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-primary/10 text-primary">
            <span className="text-3xl font-semibold">{getInitials(result.displayName)}</span>
          </div>
        )}
        <div className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-text shadow-sm">
          {formatTypes(result.providerTypes)}
        </div>
      </div>

      <div className="min-w-0 space-y-4 p-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {hasVerifiedBadge(result) ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
                <ShieldCheck className="h-3.5 w-3.5" />
                Reviewed
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1 text-sm font-medium text-text-muted">
              <MapPin className="h-4 w-4" />
              {formatLocation(result)}
            </span>
          </div>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-text">{result.displayName}</h2>
          <div className="mt-1.5 flex items-center gap-1.5 text-sm">
            {result.reviewSummary?.reviewCount && result.reviewSummary.averageRating != null ? (
              <>
                <Star className="h-4 w-4 fill-warning text-warning" />
                <span className="font-semibold text-text">{result.reviewSummary.averageRating.toFixed(1)}</span>
                <span className="text-text-muted">{result.reviewSummary.reviewCount} reviews</span>
              </>
            ) : (
              <span className="font-medium text-text-muted">New on TARK</span>
            )}
          </div>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-text-muted">{shortBio(result)}</p>
        </div>

        {tags.length ? (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-text-muted">
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        <div className="grid gap-2 sm:grid-cols-3">
          <FactPill icon={DollarSign} label="Rate" value={rate?.value || 'Ask'} />
          <FactPill icon={CheckCircle2} label="Experience" value={experience || 'On file'} />
          <FactPill icon={MapPin} label="Area" value={getServiceMetric(result)} />
        </div>
      </div>

      <aside className="flex flex-col justify-between gap-5 border-t border-border bg-surface p-5 lg:border-l lg:border-t-0">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">Availability</p>
          <p className="mt-2 text-sm font-semibold text-text">{getAvailability(result)}</p>
          {rate ? (
            <div className="mt-5">
              <p className="text-2xl font-semibold tracking-tight text-text">{rate.value}</p>
              <p className="text-xs font-medium text-text-muted">{rate.label}</p>
            </div>
          ) : null}
        </div>
        <Button onClick={onView} rightIcon={<ArrowRight className="h-4 w-4" />} fullWidth>
          View Profile
        </Button>
      </aside>
    </article>
  );
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className="mt-1 text-xl font-semibold text-text">{value}</p>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-white px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</p>
      <p className="mt-1 text-lg font-semibold text-text">{value}</p>
    </div>
  );
}

function FactPill({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Clock3;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-semibold text-text">{value}</p>
    </div>
  );
}
